<div
  class="border border-neutral-4 rounded-xl overflow-hidden shadow-xs bg-neutral-1 flex flex-col flex-1 min-h-0 w-full h-full select-none"
>
  <!-- En-tête des jours de la semaine -->
  <div
    data-calendar-week-header
    [class]="settings().showWeekends ? 'grid-cols-[60px_repeat(7,1fr)]' : 'grid-cols-[60px_repeat(5,1fr)]'"
    class="grid border-b border-neutral-4 bg-neutral-2/70 text-center shrink-0"
  >
    <div class="border-r border-neutral-4 py-3 text-xs font-semibold text-neutral-10">Heure</div>

    @for (day of weekDays(); track day.isoDate) {
      <div
        [class]="day.isToday ? 'bg-primary-1/40' : ''"
        class="py-2.5 px-1 border-r last:border-r-0 border-neutral-4 flex flex-col items-center justify-center gap-0.5"
      >
        <span class="text-[11px] font-semibold uppercase tracking-wider text-neutral-11">
          {{ day.dayName.slice(0, 3) }}
        </span>
        <span
          [class]="day.isToday ? 'bg-primary-9 text-white font-bold' : 'text-neutral-12 font-medium'"
          class="w-6 h-6 rounded-full text-xs flex items-center justify-center"
        >
          {{ day.dayNumber }}
        </span>
      </div>
    }
  </div>

  <!-- Ligne dédiée aux événements sur la journée entière (jours fériés, congés) -->
  @if (hasAnyAllDayEvents()) {
    <div
      [class]="settings().showWeekends ? 'grid-cols-[60px_repeat(7,1fr)]' : 'grid-cols-[60px_repeat(5,1fr)]'"
      class="grid border-b border-neutral-4 bg-neutral-2/50 text-xs shrink-0 select-none"
    >
      <div
        class="border-r border-neutral-4 py-2 px-1 text-[10px] font-semibold text-neutral-10 text-right pr-2 flex items-center justify-end"
      >
        Journée
      </div>
      @for (day of weekDays(); track day.isoDate; let dayIdx = $index) {
        <div class="border-r last:border-r-0 border-neutral-4/70 p-1 flex flex-col gap-1 min-h-[30px]">
          @for (evt of allDayEventsByDay()[dayIdx]; track evt.id) {
            <div
              role="button"
              tabindex="0"
              (click)="onEventClick(evt, $event)"
              (keydown.enter)="onEventClick(evt, $event)"
              [class]="getCategoryStyle(evt.category)"
              [matTooltip]="evt.title"
              class="px-1.5 py-0.5 rounded text-[11px] font-medium border truncate shadow-2xs cursor-pointer active:scale-95 transition-transform"
            >
              {{ evt.title }}
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- Grille horaire scrollable -->
  <div data-calendar-week-scroll class="relative overflow-y-auto flex-1 min-h-0">
    <div
      [class]="settings().showWeekends ? 'grid-cols-[60px_repeat(7,1fr)]' : 'grid-cols-[60px_repeat(5,1fr)]'"
      class="grid relative"
      [style.height.px]="timeSlots().length * hourHeightPx"
    >
      <!-- Colonne des heures (Gutter gauche) -->
      <div data-calendar-week-header class="border-r border-neutral-4 flex flex-col bg-neutral-2/30">
        @for (slot of timeSlots(); track slot.hour) {
          <div
            [style.height.px]="hourHeightPx"
            class="text-[11px] font-medium text-neutral-10 text-right pr-2 pt-1 border-b border-neutral-3/50 select-none"
          >
            {{ slot.label }}
          </div>
        }
      </div>

      <!-- 7 Colonnes des jours -->
      @for (day of weekDays(); track day.isoDate; let dayIdx = $index) {
        <div
          class="relative border-r last:border-r-0 border-neutral-4/70 flex flex-col"
          [class]="day.isToday ? 'bg-primary-1/10' : ''"
        >
          <!-- Lignes horizontales des heures -->
          @for (slot of timeSlots(); track slot.hour) {
            <div
              role="button"
              tabindex="0"
              (click)="onSlotClicked(day, slot.hour)"
              (keydown.enter)="onSlotClicked(day, slot.hour)"
              [style.height.px]="hourHeightPx"
              class="border-b border-neutral-3/60 hover:bg-neutral-2/60 transition-colors cursor-pointer"
            ></div>
          }

          <!-- Indicateur heure courante (si ce jour est aujourd'hui) -->
          @if (currentTimePosition().show && currentTimePosition().dayIndex === dayIdx) {
            <div
              class="absolute left-0 right-0 z-20 pointer-events-none flex items-center"
              [style.top.px]="currentTimePosition().topPx"
            >
              <span class="w-2.5 h-2.5 rounded-full bg-primary-9 -ml-1.5 shadow-xs"></span>
              <span class="h-0.5 bg-primary-9 w-full"></span>
            </div>
          }

          <!-- Événements positionnés dans ce jour -->
          @for (pe of getEventsForDay(dayIdx); track pe.event.id) {
            <div
              role="button"
              tabindex="0"
              (click)="eventClick.emit(pe.event); $event.stopPropagation()"
              (keydown.enter)="eventClick.emit(pe.event); $event.stopPropagation()"
              [class]="getCategoryStyle(pe.event.category)"
              [style.top.px]="pe.topPx"
              [style.height.px]="pe.heightPx"
              class="absolute left-1 right-1 z-10 p-1.5 rounded-lg border shadow-xs flex flex-col justify-start overflow-hidden cursor-pointer transition-all hover:scale-[1.02] hover:z-30"
              [matTooltip]="pe.event.title + ' (' + pe.timeFormatted + ')'"
            >
              <!-- Barre latérale colorée -->
              <div
                class="absolute left-0 top-0 bottom-0 w-1"
                [class]="getCategoryBarColor(pe.event.category)"
              ></div>

              <div class="pl-1.5 flex flex-col min-w-0">
                <span class="text-xs font-medium text-neutral-12 truncate leading-snug">
                  {{ pe.event.title }}
                </span>
                <span class="text-[10px] font-semibold text-neutral-11 leading-tight">
                  {{ pe.timeFormatted }}
                </span>
                @if (pe.event.location?.address && pe.heightPx > 50) {
                  <span class="text-[10px] text-neutral-10 truncate mt-0.5">
                    {{ pe.event.location?.address }}
                  </span>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>
  </div>
</div>
